import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { ArrowLeft, Loader2, TrendingDown, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import Breadcrumb from '../../Common/Breadcrumb';
import ClientMixPanel from '../Partners/ClientMixPanel';
import { Actions as COMMERCIAL } from '../../../store/actions/commercialActions';
import { toggleAlertMessage } from '../../../store/action';

const money = (value, currency = 'GBP') =>
  value == null || value === ''
    ? '—'
    : new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(value));

const pct = (value) => (value == null ? '—' : `${value > 0 ? '+' : ''}${value}%`);

const HEALTH_LABELS = {
  paying: 'Paying',
  at_risk: 'At risk',
  churned: 'Churned',
  warm: 'Warm / not yet',
  demo: 'Demo',
};

/** Active referred with no paid history yet — glance label (still churned under R-6120). */
const isWarmCustomer = (c) =>
  c &&
  !c.demo &&
  c.health === 'churned' &&
  String(c.orgStatus || '').toLowerCase() === 'active' &&
  !c.lastInvoice &&
  Number(c.paidMonths || 0) === 0;

const displayHealth = (c) => (isWarmCustomer(c) ? 'warm' : c.health);

const CHART = {
  attributed: '#1B5E4A',
  share: '#C4A35A',
  paying: '#2F7D5B',
  atRisk: '#C9852A',
  churned: '#B42318',
  warm: '#94A3B8',
  grid: 'rgba(27, 94, 74, 0.12)',
  label: '#5C6B66',
};

const NUDGE_TEMPLATES = [
  { id: 'dormant_intros', label: 'Dormant — ask for intros' },
  { id: 'celebrate_milestone', label: 'Celebrate milestone' },
  { id: 'complete_bank_tax', label: 'Complete bank / tax' },
  { id: 'share_materials', label: 'Share referral materials' },
];

const healthVariant = (h) => {
  if (h === 'paying') return 'default';
  if (h === 'at_risk') return 'secondary';
  if (h === 'churned') return 'destructive';
  if (h === 'warm') return 'outline';
  return 'outline';
};

const chartBase = {
  credits: { enabled: false },
  exporting: { enabled: false },
  chart: {
    backgroundColor: 'transparent',
    style: { fontFamily: 'inherit' },
    spacing: [12, 8, 8, 8],
  },
  title: { text: undefined },
  legend: {
    align: 'left',
    verticalAlign: 'top',
    layout: 'horizontal',
    itemStyle: { color: CHART.label, fontWeight: '500', fontSize: '12px' },
    symbolRadius: 2,
  },
  tooltip: { shared: true, useHTML: true, borderRadius: 8, backgroundColor: '#fff' },
};

const AmbassadorOverview = ({
  getAmbassadorOverview,
  updateAmbassadorEngagement,
  sendAmbassadorPartnerNudge,
  showAlert,
}) => {
  const { ambassadorOrgId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [confirmTemplate, setConfirmTemplate] = useState(null);
  const [busy, setBusy] = useState(false);

  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const res = await getAmbassadorOverview(ambassadorOrgId);
    setLoading(false);
    if (!res.ok) {
      const msg =
        res.error === 'not_found' || res.status === 404
          ? 'No accepted Ambassador Agreement on this organisation — Overview needs a completed agreement (and referral links).'
          : typeof res.error === 'string'
            ? res.error
            : 'Could not load Overview';
      setLoadError(msg);
      showAlert(true, msg, 'error');
      return;
    }
    setData(res.data);
    setNote(res.data?.engagement?.lastTouchNote || '');
    setFollowUp(res.data?.engagement?.nextFollowUpAt || '');
  }, [ambassadorOrgId, getAmbassadorOverview, showAlert]);

  useEffect(() => {
    load();
  }, [load]);

  const saveEngagement = async () => {
    setBusy(true);
    const res = await updateAmbassadorEngagement(ambassadorOrgId, {
      lastTouchNote: note,
      nextFollowUpAt: followUp || null,
    });
    setBusy(false);
    if (!res.ok) {
      showAlert(true, res.error || 'Could not save engagement', 'error');
      return;
    }
    showAlert(true, 'Engagement saved', 'success');
    load();
  };

  const sendNudge = async (template) => {
    setBusy(true);
    const res = await sendAmbassadorPartnerNudge(ambassadorOrgId, template);
    setBusy(false);
    setConfirmTemplate(null);
    if (!res.ok) {
      showAlert(true, res.error || 'Nudge failed', 'error');
      return;
    }
    const outcome = res.data?.nudge?.outcome;
    const reused = res.data?.reused;
    showAlert(
      true,
      reused ? 'Already sent today — showing prior outcome' : outcome === 'accepted' ? 'Email nudge sent' : `Nudge ${outcome}`,
      outcome === 'accepted' || reused ? 'success' : 'error'
    );
    load();
  };

  const currency = data?.payoutCurrency || 'GBP';
  const yoy = data?.yoyAttributedPct;
  const yoyUp = typeof yoy === 'number' && yoy > 0;
  const yoyDown = typeof yoy === 'number' && yoy < 0;

  const quarterlyChartOptions = useMemo(() => {
    const series = data?.quarterly || [];
    const categories = series.map((q) => q.label);
    const attributed = series.map((q) => Number(q.attributed || 0));
    const share = series.map((q) => Number(q.share || 0));
    const hasData = attributed.some((v) => v > 0) || share.some((v) => v > 0);

    return {
      ...chartBase,
      chart: { ...chartBase.chart, type: 'areaspline', height: 260 },
      xAxis: {
        categories,
        tickLength: 0,
        lineColor: CHART.grid,
        labels: {
          style: { color: CHART.label, fontSize: '11px' },
          rotation: categories.length > 8 ? -35 : 0,
        },
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: CHART.grid,
        labels: {
          style: { color: CHART.label, fontSize: '11px' },
          formatter() {
            return money(this.value, currency);
          },
        },
      },
      tooltip: {
        ...chartBase.tooltip,
        formatter() {
          const rows = (this.points || [])
            .map(
              (p) =>
                `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${money(p.y, currency)}</b>`
            )
            .join('<br/>');
          return `<span style="font-size:12px">${this.x}</span><br/>${rows}`;
        },
      },
      plotOptions: {
        areaspline: {
          fillOpacity: 0.18,
          marker: { enabled: false, symbol: 'circle', radius: 3, states: { hover: { enabled: true, radius: 5 } } },
          lineWidth: 2.5,
        },
      },
      series: [
        { name: 'Attributed', data: attributed, color: CHART.attributed, zIndex: 2 },
        { name: 'Share', data: share, color: CHART.share, zIndex: 1 },
      ],
      subtitle: hasData
        ? undefined
        : { text: 'No attributed revenue in this window yet', style: { color: CHART.label } },
    };
  }, [data?.quarterly, currency]);

  const portfolioMixOptions = useMemo(() => {
    const customers = (data?.customers || []).filter((c) => !c.demo);
    const paying = customers.filter((c) => displayHealth(c) === 'paying').length;
    const atRisk = customers.filter((c) => displayHealth(c) === 'at_risk').length;
    const warm = customers.filter((c) => displayHealth(c) === 'warm').length;
    const churned = customers.filter((c) => displayHealth(c) === 'churned').length;
    const categories = ['Paying', 'At risk', 'Churned', 'Warm / not yet'];
    const values = [paying, atRisk, churned, warm];
    const colors = [CHART.paying, CHART.atRisk, CHART.churned, CHART.warm];

    return {
      ...chartBase,
      chart: { ...chartBase.chart, type: 'column', height: 260 },
      legend: { enabled: false },
      xAxis: {
        categories,
        tickLength: 0,
        lineColor: CHART.grid,
        labels: { style: { color: CHART.label, fontSize: '11px' } },
      },
      yAxis: {
        title: { text: undefined },
        allowDecimals: false,
        gridLineColor: CHART.grid,
        min: 0,
        labels: { style: { color: CHART.label, fontSize: '11px' } },
      },
      tooltip: {
        ...chartBase.tooltip,
        shared: false,
        formatter() {
          return `<b>${this.x}</b><br/>${this.y} customer${this.y === 1 ? '' : 's'}`;
        },
      },
      plotOptions: {
        column: {
          borderRadius: 4,
          borderWidth: 0,
          colorByPoint: true,
          pointPadding: 0.15,
          groupPadding: 0.1,
        },
      },
      series: [{ name: 'Customers', data: values.map((y, i) => ({ y, color: colors[i] })) }],
    };
  }, [data?.customers]);

  if (loading && !data) {
    return (
      <div className='flex items-center gap-2 text-muted-foreground p-8'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading Overview…
      </div>
    );
  }

  if (!data) {
    return (
      <div className='p-6 space-y-3'>
        <Button variant='ghost' onClick={() => history.push('/admin/partners/ambassadors?tab=portfolio')}>
          <ArrowLeft size={16} strokeWidth={1.5} className='mr-2' />
          Back to Ambassadors
        </Button>
        <p className='text-muted-foreground'>{loadError || 'Overview not available.'}</p>
        <p className='text-sm text-muted-foreground'>
          Programme charts can still list firms with synthetic signals; Overview only loads after an Ambassador Agreement is accepted.
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-6 p-4 md:p-6'>
      <Breadcrumb
        items={[
          { label: 'Admin', link: '/admin' },
          { label: 'Ambassadors', link: '/admin/ambassadors' },
          { label: data.name || 'Overview' },
        ]}
      />

      <div className='flex flex-wrap items-center gap-3'>
        <Button variant='ghost' size='sm' onClick={() => history.push('/admin/ambassadors')}>
          <ArrowLeft size={16} strokeWidth={1.5} className='mr-2' />
          Back to Ambassadors
        </Button>
        <Badge variant={data.bankDetailsStatus === 'verified' ? 'default' : 'secondary'}>
          Bank {data.bankDetailsStatus || 'missing'}
        </Badge>
        <Badge variant={data.minimumMet ? 'default' : 'secondary'}>
          {data.minimumMet ? 'Minimum met' : 'Minimum not met'}
        </Badge>
        <Badge variant={data.selfBillingOk ? 'default' : 'secondary'}>
          {data.selfBillingOk ? 'Self-billing OK' : 'Self-billing incomplete'}
        </Badge>
        <Badge variant={data.taxOk ? 'default' : 'secondary'}>{data.taxOk ? 'Tax OK' : 'Tax incomplete'}</Badge>
      </div>

      <div>
        <h1 className='text-2xl font-semibold text-foreground'>{data.name}</h1>
        <p className='text-sm text-muted-foreground'>
          Payout {currency}
          {data.mostRecentLinkAt ? ` · Last referred link ${data.mostRecentLinkAt}` : ''}
        </p>
      </div>

      {(() => {
        const customers = (data.customers || []).filter((c) => !c.demo);
        const payingN = customers.filter((c) => displayHealth(c) === 'paying').length;
        const atRiskN = customers.filter((c) => displayHealth(c) === 'at_risk').length;
        const churnedN = customers.filter((c) => displayHealth(c) === 'churned').length;
        return (
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Referred</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums'>{customers.length}</CardContent>
        </Card>
        <Card className='border-border/80 border-l-4' style={{ borderLeftColor: CHART.paying }}>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Paying</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums' style={{ color: CHART.paying }}>
            {payingN}
          </CardContent>
        </Card>
        <Card className='border-border/80 border-l-4' style={{ borderLeftColor: CHART.atRisk }}>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>At risk</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums' style={{ color: CHART.atRisk }}>
            {atRiskN}
          </CardContent>
        </Card>
        <Card className='border-border/80 border-l-4' style={{ borderLeftColor: CHART.churned }}>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Churned</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums' style={{ color: CHART.churned }}>
            {churnedN}
          </CardContent>
        </Card>
      </div>
        );
      })()}

      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Attributed YoY</CardTitle>
          </CardHeader>
          <CardContent
            className={`text-2xl font-semibold tabular-nums flex items-center gap-2 ${
              yoyUp ? 'text-success' : yoyDown ? 'text-destructive' : ''
            }`}>
            {yoyUp ? <TrendingUp size={20} strokeWidth={1.5} /> : null}
            {yoyDown ? <TrendingDown size={20} strokeWidth={1.5} /> : null}
            {pct(yoy)}
          </CardContent>
        </Card>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Median paid tenure</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums'>{data.medianPaidTenureMonths ?? 0} mo</CardContent>
        </Card>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Customer LTV (attr.)</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums'>{money(data.attributedLtv, currency)}</CardContent>
        </Card>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Share paid to date</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums' style={{ color: CHART.share }}>
            {money(data.sharePaidToDate, currency)}
          </CardContent>
        </Card>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <Card className='border-border/80'>
          <CardHeader className='pb-1'>
            <CardTitle>Attributed revenue & share</CardTitle>
            <p className='text-xs text-muted-foreground font-normal'>Quarterly · windows end {data.windowEnd}</p>
          </CardHeader>
          <CardContent>
            <HighchartsReact highcharts={Highcharts} options={quarterlyChartOptions} />
          </CardContent>
        </Card>
        <Card className='border-border/80'>
          <CardHeader className='pb-1'>
            <CardTitle>Health mix</CardTitle>
            <p className='text-xs text-muted-foreground font-normal'>
              Paying = Active + open paid service period (annual OK)
            </p>
          </CardHeader>
          <CardContent>
            <HighchartsReact highcharts={Highcharts} options={portfolioMixOptions} />
          </CardContent>
        </Card>
      </div>

      <ClientMixPanel mix={data.clientMix} title='Plan, FTE & add-ons (referred)' />

      <Card>
        <CardHeader>
          <CardTitle>Referred customers</CardTitle>
        </CardHeader>
        <CardContent className='overflow-x-auto'>
          <Table>
            <TableHeader>
              <TableRow className='border-border'>
                <TableHead>Customer</TableHead>
                <TableHead>Linked</TableHead>
                <TableHead>Org status</TableHead>
                <TableHead>Health</TableHead>
                <TableHead>Billing</TableHead>
                <TableHead className='text-right'>Paid months</TableHead>
                <TableHead className='text-right'>Attr. LTV</TableHead>
                <TableHead className='text-right'>Share paid</TableHead>
                <TableHead>Last invoice</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data.customers || [])
                .filter((c) => !c.demo)
                .map((c) => (
                  <TableRow key={c.customerOrgId} className='border-border'>
                    <TableCell className='font-medium'>{c.name}</TableCell>
                    <TableCell>{c.linkedFrom}</TableCell>
                    <TableCell>{c.orgStatus}</TableCell>
                    <TableCell>
                      <Badge variant={healthVariant(displayHealth(c))}>
                        {HEALTH_LABELS[displayHealth(c)] || c.health}
                      </Badge>
                    </TableCell>
                    <TableCell className='text-muted-foreground text-sm'>
                      {isWarmCustomer(c) ? 'Linked — no paid invoice yet' : c.billingHint}
                    </TableCell>
                    <TableCell className='text-right'>{c.paidMonths}</TableCell>
                    <TableCell className='text-right'>{money(c.attributedLtv, currency)}</TableCell>
                    <TableCell className='text-right'>{money(c.sharePaid, currency)}</TableCell>
                    <TableCell>{c.lastInvoice || '—'}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <p className='text-xs text-muted-foreground mt-2'>
            Paying uses Active + open paid service period (annual / multi-year mid-term stays Paying).
          </p>
        </CardContent>
      </Card>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <Card>
          <CardHeader>
            <CardTitle>Relationship</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            <p className='text-sm'>
              <span className='font-medium'>Last touch:</span>{' '}
              {data.engagement?.lastTouchAt
                ? `${String(data.engagement.lastTouchAt).slice(0, 10)} — ${data.engagement.lastTouchNote || ''}`
                : '—'}
            </p>
            <p className='text-sm'>
              <span className='font-medium'>Suggested play:</span> {data.suggestedPlay || '—'}
            </p>
            <div>
              <Label htmlFor='touch-note'>Last-touch note</Label>
              <Input id='touch-note' value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div>
              <Label htmlFor='follow-up'>Next follow-up</Label>
              <Input id='follow-up' type='date' value={followUp || ''} onChange={(e) => setFollowUp(e.target.value)} />
            </div>
            <Button size='sm' loading={busy} onClick={saveEngagement}>
              Save engagement
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Trigger email nudge</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            <p className='text-sm text-muted-foreground'>
              Preview and send once by email. No schedules, no in-app, not Customer.io.
            </p>
            <div className='flex flex-wrap gap-2'>
              {NUDGE_TEMPLATES.map((t) => (
                <Button key={t.id} size='sm' variant='outline' disabled={busy} onClick={() => setConfirmTemplate(t.id)}>
                  {t.label}
                </Button>
              ))}
            </div>
            {confirmTemplate && (
              <div className='rounded-md border border-border p-3 space-y-2 bg-muted/30'>
                <p className='text-sm'>
                  Send <span className='font-medium'>{confirmTemplate}</span> to Org Admins of this Ambassador?
                </p>
                <div className='flex gap-2'>
                  <Button size='sm' loading={busy} onClick={() => sendNudge(confirmTemplate)}>
                    Confirm send
                  </Button>
                  <Button size='sm' variant='ghost' onClick={() => setConfirmTemplate(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  getAmbassadorOverview: (id) => dispatch(COMMERCIAL.getAmbassadorOverview(id)),
  updateAmbassadorEngagement: (id, body) => dispatch(COMMERCIAL.updateAmbassadorEngagement(id, body)),
  sendAmbassadorPartnerNudge: (id, template) => dispatch(COMMERCIAL.sendAmbassadorPartnerNudge(id, template)),
  showAlert: (show, message, type) => dispatch(toggleAlertMessage(show, message, type)),
});

export default connect(null, mapDispatchToProps)(AmbassadorOverview);
